import posthog from "posthog-js"
import { BROWSER_STORAGE_KEYS } from "utils/constants"

type Properties = Record<string, unknown>

type SentCache = { distinctId: string; properties: Properties }

/**
 * Batches PostHog person property updates into a single `$set` event.
 *
 * Every `posthog.setPersonProperties` call is a billed event. Profile data
 * used to be sent one key at a time on every page load and refetch, which
 * came to about 25 `$set` events per session. Calls made in the same tick
 * (for example all the effects of one render) are merged, and values that
 * were already sent for this user in this browser session are skipped.
 */
let pending: Properties = {}
let flushScheduled = false

const readCache = (): SentCache | null => {
  try {
    const raw = sessionStorage.getItem(
      BROWSER_STORAGE_KEYS.postHogPersonProperties
    )
    return raw ? (JSON.parse(raw) as SentCache) : null
  } catch {
    return null
  }
}

const writeCache = (cache: SentCache) => {
  try {
    sessionStorage.setItem(
      BROWSER_STORAGE_KEYS.postHogPersonProperties,
      JSON.stringify(cache)
    )
  } catch {
    // Storage can be unavailable (private mode, cross-origin iframe)
  }
}

export const flushPersonProperties = () => {
  flushScheduled = false
  const queued = pending
  pending = {}

  try {
    const distinctId = posthog.get_distinct_id()
    const cache = readCache()
    const sent = cache?.distinctId === distinctId ? cache.properties : {}

    const changed = Object.fromEntries(
      Object.entries(queued).filter(
        ([key, value]) =>
          !(key in sent) || JSON.stringify(sent[key]) !== JSON.stringify(value)
      )
    )
    if (Object.keys(changed).length === 0) return

    posthog.setPersonProperties(changed)
    writeCache({ distinctId, properties: { ...sent, ...changed } })
  } catch (e) {
    console.warn("Error setting PostHog person properties:", queued, e)
  }
}

export const queuePersonProperties = (properties: Properties) => {
  pending = { ...pending, ...properties }
  if (flushScheduled) return
  flushScheduled = true
  queueMicrotask(flushPersonProperties)
}

export const clearSentPersonProperties = () => {
  pending = {}
  try {
    sessionStorage.removeItem(BROWSER_STORAGE_KEYS.postHogPersonProperties)
  } catch {
    // Storage can be unavailable (private mode, cross-origin iframe)
  }
}
